import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useRef, useState } from 'react';

// ─── Supported fields with display info ──────────────────────────────────────

const FIELDS = [
  {
    name: 'tracking_number',
    label: 'Tracking Number',
    description: 'Package tracking number'
  },
  {
    name: 'customer_name',
    label: 'Customer Name',
    description: 'Recipient / customer name'
  },
  { name: 'address', label: 'Address', description: 'Delivery address' },
  { name: 'city', label: 'City', description: 'City in delivery address' },
  {
    name: 'scanned_at_address',
    label: 'Scanned At',
    description: 'Location where package was scanned'
  },
  {
    name: 'company_name',
    label: 'Company Name',
    description: 'Carrier / company name (e.g. FedEx, UPS)'
  },
  {
    name: 'order_number',
    label: 'Order Number',
    description: 'Associated order / PO number'
  },
  {
    name: 'direction_type',
    label: 'Direction Type',
    description: 'incoming or outgoing'
  },
  { name: 'barcode', label: 'Barcode', description: 'Package barcode value' },
  { name: 'upc', label: 'UPC', description: 'Item UPC code' },
  {
    name: 'imei',
    label: 'IMEI',
    description: 'Item IMEI (matches IMEI 1 / IMEI 2)'
  },
  {
    name: 'sn',
    label: 'Serial Number',
    description: 'Item serial number (SN / MSN)'
  },
  { name: 'mpn', label: 'MPN', description: 'Item MPN / MSKU' },
  {
    name: 'transparency_code',
    label: 'Transparency Code',
    description: 'Item Transparency QR code'
  },
  {
    name: 'item',
    label: 'Item Info',
    description: 'Match any Item Info field (UPC, IMEI, SN, MPN…)'
  }
];

const OPERATORS = ['AND', 'OR', 'NOT'];
const COMPARATORS = ['=', '!='];
const DIRECTION_VALUES = ['incoming', 'outgoing'];

const SAMPLE_QUERIES = [
  {
    label: 'Find by tracking number',
    query: 'tracking_number="1Z999AA10123456784"'
  },
  {
    label: 'Find incoming packages from FedEx',
    query: 'company_name="FedEx" AND direction_type="incoming"'
  },
  {
    label: 'Find by recipient with address filter',
    query: 'customer_name="John" AND address="Los Angeles"'
  },
  {
    label: 'Exclude outgoing packages',
    query: 'NOT direction_type="outgoing"'
  },
  {
    label: 'Multiple conditions with grouping',
    query: '(address="Los Angeles" OR city="LA") AND direction_type="incoming"'
  },
  {
    label: 'Find by order number',
    query: 'order_number="ORD-001"'
  },
  {
    label: 'Find by item UPC',
    query: 'upc="012345678905"'
  },
  {
    label: 'Find by item IMEI',
    query: 'imei="356938035643809"'
  }
];

// ─── Lightweight JQL cursor analyser ─────────────────────────────────────────

function analysePosition(text) {
  const trimmed = text.trimStart();
  if (!trimmed) return { stage: 'field', prefix: '' };
  const tokens = tokeniseLeft(trimmed);
  if (tokens.length === 0) return { stage: 'field', prefix: '' };
  const last = tokens[tokens.length - 1];
  if (last.complete) {
    const afterLast = text.slice(last.end).trimStart();
    const opMatch = afterLast.match(/^(AND|OR|NOT)?(\s*)(.*)$/i);
    const partial = opMatch ? opMatch[3] : '';
    if (!afterLast || /^\s*$/.test(afterLast)) {
      return { stage: 'operator', prefix: '' };
    }
    const upper = afterLast.trimStart().toUpperCase();
    if (OPERATORS.some((op) => op.startsWith(upper) && upper !== op)) {
      return { stage: 'operator', prefix: afterLast.trimStart() };
    }
    if (OPERATORS.some((op) => upper.trimEnd() === op)) {
      const rest = afterLast.replace(/^(AND|OR|NOT)\s*/i, '');
      return { stage: 'field', prefix: rest };
    }
    return { stage: 'field', prefix: partial };
  }
  switch (last.state) {
    case 'field':
      return { stage: 'field', prefix: last.value };
    case 'comparator':
      return { stage: 'comparator', prefix: last.value };
    case 'value':
      return { stage: 'value', prefix: last.value, field: last.field };
    default:
      return { stage: 'none', prefix: '' };
  }
}

function tokeniseLeft(text) {
  const tokens = [];
  let i = 0;
  const skipWs = () => {
    while (i < text.length && /\s/.test(text[i])) i++;
  };

  while (i < text.length) {
    skipWs();
    if (i >= text.length) break;

    const opMatch = text.slice(i).match(/^(AND|OR|NOT)(?=\s|$)/i);
    if (opMatch) {
      i += opMatch[1].length;
      skipWs();
      continue;
    }

    if (/[a-zA-Z_]/.test(text[i])) {
      let name = '';
      while (i < text.length && /[a-zA-Z0-9_]/.test(text[i])) name += text[i++];
      skipWs();
      const compMatch = text.slice(i).match(/^(!?=)/);
      if (!compMatch) {
        tokens.push({ state: 'field', value: name, complete: false, end: i });
        break;
      }
      const comp = compMatch[1];
      i += comp.length;
      skipWs();
      if (i >= text.length) {
        tokens.push({
          state: 'value',
          value: '',
          field: name,
          complete: false,
          end: i
        });
        break;
      }
      if (text[i] === '"' || text[i] === "'") {
        const quote = text[i++];
        let val = '';
        while (i < text.length && text[i] !== quote) {
          if (text[i] === '\\') i++;
          val += text[i++];
        }
        if (i < text.length) {
          i++;
          tokens.push({
            state: 'condition',
            value: val,
            field: name,
            complete: true,
            end: i
          });
        } else {
          tokens.push({
            state: 'value',
            value: val,
            field: name,
            complete: false,
            end: i
          });
          break;
        }
      } else {
        let val = '';
        while (i < text.length && !/\s/.test(text[i])) val += text[i++];
        tokens.push({
          state: 'condition',
          value: val,
          field: name,
          complete: true,
          end: i
        });
      }
    } else {
      let partial = '';
      while (i < text.length && !/\s/.test(text[i])) partial += text[i++];
      tokens.push({ state: 'field', value: partial, complete: false, end: i });
      break;
    }
  }
  return tokens;
}

// ─── Suggestion builders ──────────────────────────────────────────────────────

function buildSuggestions({ stage, prefix, field }) {
  const pfx = (prefix || '').toLowerCase();
  if (stage === 'field') {
    return FIELDS.filter(
      (f) => f.name.startsWith(pfx) || f.label.toLowerCase().startsWith(pfx)
    ).map((f) => ({ label: f.name, hint: f.label, insert: `${f.name}=` }));
  }
  if (stage === 'comparator') {
    return COMPARATORS.filter((c) => c.startsWith(pfx)).map((c) => ({
      label: c,
      hint: '',
      insert: `${c}"`
    }));
  }
  if (stage === 'value' && field === 'direction_type') {
    return DIRECTION_VALUES.filter((v) => v.startsWith(pfx)).map((v) => ({
      label: v,
      hint: '',
      insert: `${v}"`
    }));
  }
  if (stage === 'operator') {
    return OPERATORS.filter((op) => op.startsWith(pfx.toUpperCase())).map(
      (op) => ({ label: op, hint: '', insert: `${op} ` })
    );
  }
  return [];
}

// ─── JQL Help Popover ─────────────────────────────────────────────────────────

function JqlHelpPopover({ anchorEl, onClose }) {
  const [copiedIdx, setCopiedIdx] = useState(null);

  const handleCopy = (query, idx) => {
    navigator.clipboard.writeText(query).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: { width: 520, maxHeight: 560, overflowY: 'auto', p: 2.5 }
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        JQL Search Guide
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        JQL (Query Language) lets you filter packages using field conditions.
        Searches are
        <strong> case-insensitive</strong> and use{' '}
        <strong>partial matching</strong> — you don't need to type the full
        value.
      </Typography>

      <Divider sx={{ mb: 1.5 }} />

      {/* Supported fields */}
      <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
        Supported Fields
      </Typography>
      <Table size="small" sx={{ mb: 1.5 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>
              Field
            </TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>
              Description
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {FIELDS.map((f) => (
            <TableRow key={f.name}>
              <TableCell
                sx={{ fontFamily: 'monospace', fontSize: 12, py: 0.5 }}
              >
                {f.name}
              </TableCell>
              <TableCell
                sx={{ fontSize: 12, py: 0.5, color: 'text.secondary' }}
              >
                {f.description}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ mb: 1.5 }} />

      {/* Operators */}
      <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
        Operators
      </Typography>
      <Table size="small" sx={{ mb: 1.5 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>
              Operator
            </TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>
              Meaning
            </TableCell>
            <TableCell sx={{ fontWeight: 700, py: 0.5, fontSize: 12 }}>
              Example
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            {
              op: '=',
              meaning: 'Matches (partial, case-insensitive)',
              example: 'company_name="FedEx"'
            },
            {
              op: '!=',
              meaning: 'Does not match',
              example: 'direction_type!="outgoing"'
            },
            {
              op: 'AND',
              meaning: 'Both conditions must be true',
              example: 'company_name="UPS" AND direction_type="incoming"'
            },
            {
              op: 'OR',
              meaning: 'Either condition can be true',
              example: 'address="LA" OR city="Los Angeles"'
            },
            {
              op: 'NOT',
              meaning: 'Negates the following condition',
              example: 'NOT direction_type="incoming"'
            }
          ].map(({ op, meaning, example }) => (
            <TableRow key={op}>
              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  py: 0.5,
                  fontSize: 12
                }}
              >
                {op}
              </TableCell>
              <TableCell
                sx={{ fontSize: 12, py: 0.5, color: 'text.secondary' }}
              >
                {meaning}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  py: 0.5,
                  color: 'text.primary'
                }}
              >
                {example}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ mb: 1.5 }} />

      {/* Sample Queries */}
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Sample Queries
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {SAMPLE_QUERIES.map((sq, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 1,
              px: 1.5,
              py: 0.75
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {sq.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {sq.query}
              </Typography>
            </Box>
            <Tooltip
              title={copiedIdx === idx ? 'Copied!' : 'Copy'}
              placement="left"
            >
              <IconButton
                size="small"
                onClick={() => handleCopy(sq.query, idx)}
              >
                <ContentCopyIcon
                  sx={{
                    fontSize: 14,
                    color: copiedIdx === idx ? 'success.main' : 'action.active'
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary">
        💡 Tip: Use parentheses to group conditions — e.g.{' '}
        <code>
          (address=&quot;LA&quot; OR city=&quot;LA&quot;) AND
          direction_type=&quot;incoming&quot;
        </code>
      </Typography>
    </Popover>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Search bar for the Package Manager page with two modes:
 *   - Basic: plain text search across all columns (sent as `q`)
 *   - JQL:   JIRA-like structured query with autocomplete (sent as `jql`)
 *
 * Props:
 *   mode           {'basic'|'jql'}  - controlled search mode
 *   onModeChange   {fn}             - called with new mode string
 *   value          {string}         - current JQL query (controlled)
 *   onChange       {fn}             - JQL query change handler
 *   basicValue     {string}         - current basic search text (controlled)
 *   onBasicChange  {fn}             - basic search text change handler
 *   onSearch       {fn}             - called when user triggers a search
 *   onReset        {fn}             - called to clear / reset everything
 *   error          {string}         - optional JQL parse error from backend
 */
export default function JqlSearchBar({
  mode = 'basic',
  onModeChange,
  value = '',
  onChange,
  basicValue = '',
  onBasicChange,
  onSearch,
  onReset,
  error = ''
}) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // ── Basic mode handlers ───────────────────────────────────────────────────

  const handleBasicChange = (e) => {
    onBasicChange(e.target.value);
  };

  const handleBasicKeyDown = (e) => {
    if (e.key === 'Enter' && basicValue.trim()) onSearch();
  };

  // ── JQL mode handlers ─────────────────────────────────────────────────────

  const handleJqlChange = (e) => {
    const newVal = e.target.value;
    onChange(newVal);
    refreshSuggestions(newVal);
    setActiveIndex(-1);
  };

  const refreshSuggestions = (text) => {
    const pos = analysePosition(text);
    const suggs = buildSuggestions(pos);
    setSuggestions(suggs);
    setOpen(suggs.length > 0);
  };

  const handleJqlFocus = () => {
    refreshSuggestions(value);
    setActiveIndex(-1);
  };

  const handleJqlKeyDown = (e) => {
    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev < suggestions.length - 1 ? prev + 1 : 0;
          scrollItemIntoView(next);
          return next;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev > 0 ? prev - 1 : suggestions.length - 1;
          scrollItemIntoView(next);
          return next;
        });
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        applySuggestion(suggestions[activeIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setActiveIndex(-1);
        return;
      }
      if (e.key === 'Tab' && activeIndex >= 0) {
        e.preventDefault();
        applySuggestion(suggestions[activeIndex]);
        return;
      }
    }
    if (e.key === 'Enter') {
      setOpen(false);
      setActiveIndex(-1);
      if (value.trim()) onSearch();
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const scrollItemIntoView = (index) => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-suggestion-item]');
    if (items[index]) items[index].scrollIntoView({ block: 'nearest' });
  };

  const applySuggestion = (sugg) => {
    const pos = analysePosition(value);
    const prefix = pos.prefix || '';
    let base = value;
    if (prefix && base.endsWith(prefix)) {
      base = base.slice(0, base.length - prefix.length);
    } else if (pos.stage === 'value') {
      const quoteIdx = base.lastIndexOf('"');
      if (quoteIdx !== -1) base = base.slice(0, quoteIdx + 1);
    }
    let insert = sugg.insert;
    if (pos.stage === 'value' && !insert.endsWith('"')) insert = `${insert}"`;
    const newVal = `${base}${insert}`;
    onChange(newVal);
    refreshSuggestions(newVal);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  // ── Mode toggle ───────────────────────────────────────────────────────────

  const handleModeChange = (_e, newMode) => {
    if (!newMode) return; // prevent deselecting both
    onModeChange(newMode);
    setOpen(false);
    setActiveIndex(-1);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const hasValue = mode === 'basic' ? Boolean(basicValue) : Boolean(value);

  const handleClear = () => {
    onChange('');
    onBasicChange('');
    onReset();
    setOpen(false);
    setActiveIndex(-1);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Mode toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{ height: 32 }}
        >
          <ToggleButton
            value="basic"
            sx={{ px: 2, textTransform: 'none', fontSize: 13 }}
          >
            Basic
          </ToggleButton>
          <ToggleButton
            value="jql"
            sx={{ px: 2, textTransform: 'none', fontSize: 13 }}
          >
            JQL
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === 'jql' && (
          <Tooltip title="JQL Help">
            <IconButton
              size="small"
              onClick={(e) => setHelpAnchorEl(e.currentTarget)}
              sx={{ color: 'primary.main' }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Basic mode input */}
      {mode === 'basic' && (
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search by tracking number, recipient, address, company, or Item Info (UPC, IMEI…)"
          value={basicValue}
          onChange={handleBasicChange}
          onKeyDown={handleBasicKeyDown}
          helperText={
            <Typography variant="caption" color="text.secondary">
              Searches all columns including Item Info (UPC, IMEI, SN…) — switch
              to <strong>JQL</strong> mode for advanced field-specific queries
            </Typography>
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: basicValue ? (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" onClick={handleClear}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
      )}

      {/* JQL mode input */}
      {mode === 'jql' && (
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false);
            setActiveIndex(-1);
          }}
        >
          <Box sx={{ position: 'relative', width: '100%' }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              variant="outlined"
              size="small"
              placeholder='e.g. tracking_number="1Z123" AND customer_name="John Doe"'
              value={value}
              onChange={handleJqlChange}
              onFocus={handleJqlFocus}
              onKeyDown={handleJqlKeyDown}
              error={Boolean(error)}
              helperText={
                error || (
                  <Typography variant="caption" color="text.secondary">
                    Use fields like <code>tracking_number</code>,{' '}
                    <code>customer_name</code>, <code>address</code>,{' '}
                    <code>direction_type</code> with <code>AND</code> /{' '}
                    <code>OR</code> / <code>NOT</code> — click ℹ️ for help
                  </Typography>
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: hasValue ? (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end" onClick={handleClear}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />

            {open && suggestions.length > 0 && (
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1300,
                  maxHeight: 260,
                  overflowY: 'auto'
                }}
              >
                <List dense disablePadding ref={listRef}>
                  {suggestions.map((sugg, idx) => (
                    <ListItemButton
                      key={sugg.label}
                      data-suggestion-item
                      selected={idx === activeIndex}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        applySuggestion(sugg);
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {sugg.label}
                          </Typography>
                        }
                        secondary={sugg.hint || undefined}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </ClickAwayListener>
      )}

      {/* JQL Help popover */}
      <JqlHelpPopover
        anchorEl={helpAnchorEl}
        onClose={() => setHelpAnchorEl(null)}
      />
    </Box>
  );
}
